import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  consumeMagicLink as apiConsumeMagicLink,
  loginWithPin as apiLoginWithPin,
  STAFF_UNAUTHORIZED_EVENT,
} from '../api/staffPortal';
import type { StaffAuthResponse } from '../api/staffPortal';

type StaffSessionState = {
  session: StaffAuthResponse | null;
  token: string | null;
  loading: boolean;
  rememberSession: boolean;
  setRememberSession: (remember: boolean) => void;
  loginWithPin: (pin: string, merchant: string) => Promise<StaffAuthResponse>;
  consumeMagicLink: (token: string) => Promise<StaffAuthResponse>;
  updateSession: (updater: (prev: StaffAuthResponse | null) => StaffAuthResponse | null) => void;
  logout: () => void;
};

const STORAGE_KEY = 'coop_staff_session';

const StaffSessionContext = createContext<StaffSessionState | undefined>(undefined);

const readStoredSession = (): { session: StaffAuthResponse | null; token: string | null } => {
  if (typeof window === 'undefined') {
    return { session: null, token: null };
  }
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return { session: null, token: null };
  }
  if (!raw) return { session: null, token: null };
  try {
    const parsed = JSON.parse(raw) as { session: StaffAuthResponse; token: string };
    return { session: parsed.session, token: parsed.token };
  } catch {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    return { session: null, token: null };
  }
};

export function StaffSessionProvider({ children }: { children: ReactNode }) {
  const initialStored = useMemo(() => readStoredSession(), []);
  const [session, setSession] = useState<StaffAuthResponse | null>(initialStored.session);
  const [token, setToken] = useState<string | null>(initialStored.token);
  const [rememberSession, setRememberSession] = useState<boolean>(true);
  const loading = false;

  useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (rememberSession && session && token) {
      const value = JSON.stringify({ session, token });
      try {
        sessionStorage.setItem(STORAGE_KEY, value);
      } catch {
        // ignore
      }
    } else {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, [rememberSession, session, token]);

  const logout = useCallback(() => {
    setSession(null);
    setToken(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  // Drop persisted staff auth when user leaves /staff/portal scope.
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleUnauthorized = () => logout();
    window.addEventListener(STAFF_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(STAFF_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, [logout]);

  const loginWithPin = useCallback(
    async (pin: string, merchant: string) => {
      const result = await apiLoginWithPin(pin, merchant);
      setSession(result);
      setToken(result.token);
      return result;
    },
    [],
  );

  const consumeMagicLink = useCallback(async (magicToken: string) => {
    const result = await apiConsumeMagicLink(magicToken);
    setSession(result);
    setToken(result.token);
    return result;
  }, []);

  const updateSession = useCallback((updater: (prev: StaffAuthResponse | null) => StaffAuthResponse | null) => {
    setSession((prev) => updater(prev));
  }, []);

  const value = useMemo(
    () => ({
      session,
      token,
      loading,
      rememberSession,
      setRememberSession,
      loginWithPin,
      consumeMagicLink,
      updateSession,
      logout,
    }),
    [session, token, loading, rememberSession, loginWithPin, consumeMagicLink, updateSession, logout],
  );

  return <StaffSessionContext.Provider value={value}>{children}</StaffSessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStaffSession() {
  const ctx = useContext(StaffSessionContext);
  if (!ctx) {
    throw new Error('useStaffSession must be used within a StaffSessionProvider');
  }
  return ctx;
}
